/**
 * The welcome screen. Two looks, same props:
 *  - standard  a card: what the kiosk does, quick choices, phrases to try (a tap says them);
 *  - video     a waiting video filling the content area, like the old kiosk at idle: `video` if given, else the
 *              pack's (DomainPack.attract); the card when there is none.
 */
import { useEffect, useRef } from 'react';
import { z } from 'zod';
import { defineWidget, type WidgetProps } from '../contract';
import { intentSchema, run } from '../intent';
import { useApp } from '../../react/app';
import { Icon } from '../../ui-kit/Icon';
import css from '../Widgets.module.css';

const schema = z.object({
  title: z.string(),
  subtitle: z.string().optional(),
  cards: z.array(z.object({ icon: z.string(), title: z.string(), text: z.string().optional() })).max(4).default([]),
  examples: z.array(z.string()).max(4).default([]),
  examplesTitle: z.string().optional(),
  /** Quick choices for who prefers to touch (frequent destinations, operations). */
  choices: z.array(z.object({ label: z.string(), sub: z.string().optional(), icon: z.string().optional(), intent: intentSchema })).max(8).default([]),
  choicesTitle: z.string().optional(),
  /** A looping video for the `video` look, and the seconds to skip at its end (without: the pack's). */
  video: z.string().optional(),
  videoTrimEndS: z.number().min(0).optional(),
});

type Props = z.infer<typeof schema>;

/**
 * A muted looping video filling its box, slightly zoomed (a letterboxed source shows no black band).
 * `trimEndS`: the loop starts over that many seconds before the end, checked on every frame.
 */
export function AttractVideo({ src, trimEndS = 0 }: { src: string; trimEndS?: number }) {
  const ref = useRef<HTMLVideoElement>(null);
  useEffect(() => {
    const video = ref.current;
    if (!video || trimEndS <= 0) return;
    let handle = 0;
    const check = () => {
      if (Number.isFinite(video.duration) && video.currentTime >= video.duration - trimEndS) video.currentTime = 0;
      handle = video.requestVideoFrameCallback(check);
    };
    if (typeof video.requestVideoFrameCallback !== 'function') {
      video.addEventListener('timeupdate', check); // no per-frame callback: about 4 checks a second
      return () => video.removeEventListener('timeupdate', check);
    }
    handle = video.requestVideoFrameCallback(check);
    return () => video.cancelVideoFrameCallback(handle);
  }, [src, trimEndS]);
  return (
    <div className={css.attractFrame}>
      <video ref={ref} className={css.attractVideo} src={src} autoPlay loop muted playsInline aria-hidden />
    </div>
  );
}

function AttractCard({ props, api }: WidgetProps<Props>) {
  return (
    <div className={css.attract}>
      <h1 className={css.attractTitle}>{props.title}</h1>
      {props.subtitle && <p className={css.attractSubtitle}>{props.subtitle}</p>}
      {props.cards.length > 0 && (
        <div className={css.cards}>
          {props.cards.map((c) => (
            <div key={c.title} className={css.card}>
              <Icon name={c.icon} size={44} />
              <strong>{c.title}</strong>
              {c.text && <span>{c.text}</span>}
            </div>
          ))}
        </div>
      )}
      {props.choices.length > 0 && (
        <div className={css.examples}>
          {props.choicesTitle && <span className={css.examplesTitle}>{props.choicesTitle}</span>}
          <div className={css.choiceRow}>
            {props.choices.map((c) => (
              <button key={c.label} className={css.choice} onClick={() => run(api, c.intent)}>
                {c.icon && <Icon name={c.icon} size={30} />}
                <span>{c.label}</span>
                {c.sub && <small>{c.sub}</small>}
              </button>
            ))}
          </div>
        </div>
      )}
      {props.examples.length > 0 && (
        <div className={css.examples}>
          {props.examplesTitle && <span className={css.examplesTitle}>{props.examplesTitle}</span>}
          {props.examples.map((e) => (
            <button key={e} className={css.example} onClick={() => !api.readOnly && api.say(e)}>
              <Icon name="mic" size={22} />“{e}”
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

function AttractVideoLook(p: WidgetProps<Props>) {
  const own = useApp().pack.attract;
  const video = p.props.video ? { video: p.props.video, trimEndS: p.props.videoTrimEndS } : own;
  return video ? <AttractVideo src={video.video} trimEndS={video.trimEndS} /> : <AttractCard {...p} />;
}

export const attract = defineWidget({
  key: 'attract',
  schema,
  Component: AttractCard,
  variants: [{
    id: 'video',
    label: 'widget.variant.video',
    Component: AttractVideoLook,
  }],
});
